
import React, {
  useCallback,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import { useAdminScope } from "../../context/AdminScopeContext";
import { AdminButton } from "../../pages/admin/action_engine";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import {
  useWorkspaceNavigation,
} from "../../pages/admin/workspace_engine";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
  AdminSuccessState,
} from "../../pages/admin/ui_engine";
import {
  AdminFormToolbar,
} from "../../pages/admin/section_engine";
import {
  createReplenishmentProposal,
  DEFAULT_TARGET_COVERAGE_DAYS,
  getReplenishmentErrorMessage,
  prepareReplenishmentPurchaseOrder,
} from "../../services/replenishmentAdminApi";


export const REPLENISHMENT_ADMIN_PANEL_STATUS =
  Object.freeze({
    IDLE: "idle",
    LOADING: "loading",
    EMPTY: "empty",
    ERROR: "error",
    READY: "ready",
  });


function displayValue(
  value,
  fallback = "—",
) {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return fallback;
  }

  return String(value);
}


function displayBoolean(value) {
  return value ? "Oui" : "Non";
}


function lineKey(line, index) {
  return (
    line?.product_uuid
    || line?.catalog_item_uuid
    || line?.supplier_reference
    || `replenishment-line-${index}`
  );
}


function constraintKey(constraint, index) {
  return (
    constraint?.code
    || constraint?.name
    || constraint?.type
    || `replenishment-constraint-${index}`
  );
}


export default function SupplyChainReplenishmentAdminPanel() {
  const {
    selectedCompanyUuid,
  } = useAdminScope();

  const {
    openPanel,
  } = useWorkspaceNavigation();

  const [supplierUuid, setSupplierUuid] =
    useState("");
  const [productUuidsInput, setProductUuidsInput] =
    useState("");
  const [
    targetCoverageDays,
    setTargetCoverageDays,
  ] = useState(
    String(DEFAULT_TARGET_COVERAGE_DAYS),
  );

  const [status, setStatus] = useState(
    REPLENISHMENT_ADMIN_PANEL_STATUS.IDLE,
  );
  const [proposal, setProposal] = useState(null);
  const [errorMessage, setErrorMessage] =
    useState("");

  const [orderReference, setOrderReference] =
    useState("");
  const [
    preparingPurchaseOrder,
    setPreparingPurchaseOrder,
  ] = useState(false);
  const [
    preparedPurchaseOrder,
    setPreparedPurchaseOrder,
  ] = useState(null);
  const [
    purchaseOrderErrorMessage,
    setPurchaseOrderErrorMessage,
  ] = useState("");

  const generateProposal = useCallback(
    async () => {
      setStatus(
        REPLENISHMENT_ADMIN_PANEL_STATUS.LOADING,
      );
      setErrorMessage("");
      setPreparedPurchaseOrder(null);
      setPurchaseOrderErrorMessage("");

      try {
        const response =
          await createReplenishmentProposal({
            companyUuid: selectedCompanyUuid,
            supplierUuid,
            productUuids: productUuidsInput,
            targetCoverageDays,
            metadata: {
              requested_from:
                "admin_framework",
            },
          });

        const nextProposal =
          response?.proposal || null;

        setProposal(nextProposal);

        setStatus(
          nextProposal?.lines?.length
            ? REPLENISHMENT_ADMIN_PANEL_STATUS.READY
            : REPLENISHMENT_ADMIN_PANEL_STATUS.EMPTY,
        );
      } catch (error) {
        setProposal(null);
        setErrorMessage(
          getReplenishmentErrorMessage(error),
        );
        setStatus(
          REPLENISHMENT_ADMIN_PANEL_STATUS.ERROR,
        );
      }
    },
    [
      productUuidsInput,
      selectedCompanyUuid,
      supplierUuid,
      targetCoverageDays,
    ],
  );

  const preparePurchaseOrder = useCallback(
    async () => {
      if (preparingPurchaseOrder) {
        return;
      }

      setPreparingPurchaseOrder(true);
      setPreparedPurchaseOrder(null);
      setPurchaseOrderErrorMessage("");

      try {
        const response =
          await prepareReplenishmentPurchaseOrder({
            companyUuid: selectedCompanyUuid,
            supplierUuid:
              proposal?.supplier_uuid
              || supplierUuid,
            productUuids: productUuidsInput,
            targetCoverageDays,
            orderReference,
            metadata: {
              requested_from:
                "admin_framework",
            },
          });

        const nextPurchaseOrder =
          response?.purchase_order || null;

        setPreparedPurchaseOrder(
          nextPurchaseOrder,
        );

        if (nextPurchaseOrder) {
          openPanel(
            "supply_chain_purchase_orders",
            "suppliers",
            {
              initialOrderReference:
                nextPurchaseOrder.order_reference,
            },
          );
        }
      } catch (error) {
        setPurchaseOrderErrorMessage(
          getReplenishmentErrorMessage(
            error,
            "Impossible de préparer la commande fournisseur.",
          ),
        );
      } finally {
        setPreparingPurchaseOrder(false);
      }
    },
    [
      openPanel,
      orderReference,
      preparingPurchaseOrder,
      productUuidsInput,
      proposal,
      selectedCompanyUuid,
      supplierUuid,
      targetCoverageDays,
    ],
  );


  usePanelActions(
    useMemo(
      () => [
        {
          key: "generate-replenishment-proposal",
          label:
            status
              === REPLENISHMENT_ADMIN_PANEL_STATUS.LOADING
              ? "Génération en cours"
              : proposal
                ? "Regénérer la proposition"
                : "Générer la proposition",
          icon: "↻",
          onClick: generateProposal,
          disabled:
            status
            === REPLENISHMENT_ADMIN_PANEL_STATUS.LOADING
            || preparingPurchaseOrder,
        },
        {
          key: "prepare-replenishment-purchase-order",
          label: preparingPurchaseOrder
            ? "Préparation en cours"
            : "Préparer la commande",
          icon: "🧾",
          onClick: preparePurchaseOrder,
          disabled:
            !proposal
            || proposal?.is_valid !== true
            || !Array.isArray(proposal?.lines)
            || proposal.lines.length === 0
            || !proposal?.supplier_uuid
            || !orderReference.trim()
            || preparingPurchaseOrder
            || status
              === REPLENISHMENT_ADMIN_PANEL_STATUS.LOADING,
        },
      ],
      [
        generateProposal,
        orderReference,
        preparePurchaseOrder,
        preparingPurchaseOrder,
        proposal,
        status,
      ],
    ),
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    generateProposal();
  };

  const lines = Array.isArray(proposal?.lines)
    ? proposal.lines
    : [];

  const constraints =
    Array.isArray(proposal?.constraints)
      ? proposal.constraints
      : [];


  return (
    <PanelRoot>
      <AdminFormToolbar onSubmit={handleSubmit}>
        <FieldGroup>
          <FieldLabel htmlFor="replenishment-supplier">
            Fournisseur
          </FieldLabel>
          <TextInput
            id="replenishment-supplier"
            value={supplierUuid}
            onChange={(event) => {
              setSupplierUuid(event.target.value);
            }}
            placeholder="UUID fournisseur — optionnel"
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="replenishment-coverage">
            Couverture cible
          </FieldLabel>
          <NumberInput
            id="replenishment-coverage"
            type="number"
            min="1"
            step="1"
            value={targetCoverageDays}
            onChange={(event) => {
              setTargetCoverageDays(
                event.target.value,
              );
            }}
            aria-label="Jours de couverture cible"
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="replenishment-order-reference">
            Référence commande
          </FieldLabel>
          <TextInput
            id="replenishment-order-reference"
            value={orderReference}
            onChange={(event) => {
              setOrderReference(event.target.value);
            }}
            placeholder="Ex. PO-2026-0001"
            aria-label="Référence commande"
          />
        </FieldGroup>

        <FieldGroupWide>
          <FieldLabel htmlFor="replenishment-products">
            Produits ciblés
          </FieldLabel>
          <TextArea
            id="replenishment-products"
            value={productUuidsInput}
            onChange={(event) => {
              setProductUuidsInput(
                event.target.value,
              );
            }}
            placeholder={
              "UUID produits séparés par une virgule "
              + "ou une ligne — optionnel"
            }
          />
        </FieldGroupWide>

        <AdminButton
          variant="primary"
          type="submit"
          disabled={
            status
            === REPLENISHMENT_ADMIN_PANEL_STATUS.LOADING
          }
        >
          Générer
        </AdminButton>
      </AdminFormToolbar>

      <PurchaseOrderFeedback
        aria-live="polite"
        aria-atomic="true"
      >
        {preparingPurchaseOrder && (
          <AdminLoadingState
            message="Préparation de la commande fournisseur..."
            compact
          />
        )}

        {!preparingPurchaseOrder
          && purchaseOrderErrorMessage && (
          <AdminErrorState
            message={purchaseOrderErrorMessage}
            compact
          />
        )}

        {!preparingPurchaseOrder
          && preparedPurchaseOrder && (
          <AdminSuccessState
            role="status"
            data-testid="purchase-order-success"
          >
            <strong>
              Commande fournisseur préparée
            </strong>

            <span>
              La commande{" "}
              <b>
                {displayValue(
                  preparedPurchaseOrder
                    ?.order_reference,
                )}
              </b>{" "}
              a été créée au statut{" "}
              <b>
                {displayValue(
                  preparedPurchaseOrder?.status,
                  "draft",
                )}
              </b>.
            </span>
          </AdminSuccessState>
        )}
      </PurchaseOrderFeedback>

      {status
        === REPLENISHMENT_ADMIN_PANEL_STATUS.IDLE && (
        <IntroCard>
          <strong>
            Préparer une proposition de réassort
          </strong>
          <span>
            Sélectionnez éventuellement un fournisseur,
            des produits et une couverture cible, puis
            lancez le calcul.
          </span>
        </IntroCard>
      )}

      {status
        === REPLENISHMENT_ADMIN_PANEL_STATUS.LOADING && (
        <AdminLoadingState
          message="Génération de la proposition de réassort..."
        />
      )}

      {status
        === REPLENISHMENT_ADMIN_PANEL_STATUS.ERROR && (
        <AdminErrorState
          message={errorMessage}
          compact
        />
      )}

      {status
        === REPLENISHMENT_ADMIN_PANEL_STATUS.EMPTY && (
        <>
          <AdminEmptyState
            message={
              "La proposition a été calculée, "
              + "mais aucune ligne de réassort "
              + "n’est nécessaire."
            }
          />
          {proposal && (
            <ProposalSummary proposal={proposal} />
          )}
        </>
      )}

      {status
        === REPLENISHMENT_ADMIN_PANEL_STATUS.READY && (
        <>
          <ProposalSummary proposal={proposal} />

          <Section>
            <SectionTitle>
              Lignes proposées
            </SectionTitle>

            <TableWrapper>
              <ResultTable>
                <thead>
                  <tr>
                    <th scope="col">Produit</th>
                    <th scope="col">Référence</th>
                    <th scope="col">Demandé</th>
                    <th scope="col">Optimisé</th>
                    <th scope="col">Prix HT</th>
                    <th scope="col">Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={lineKey(line, index)}>
                      <td>
                        {displayValue(
                          line.product_name
                          || line.product_uuid,
                        )}
                      </td>
                      <td>
                        {displayValue(
                          line.supplier_reference
                          || line.catalog_item_uuid,
                        )}
                      </td>
                      <td>
                        {displayValue(
                          line.requested_quantity,
                        )}
                      </td>
                      <td>
                        {displayValue(
                          line.optimized_quantity
                          ?? line.quantity,
                        )}
                      </td>
                      <td>
                        {displayValue(
                          line.unit_price_ht,
                        )}
                      </td>
                      <td>
                        {displayValue(
                          line.total_ht
                          ?? line.estimated_total_ht,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResultTable>
            </TableWrapper>
          </Section>

          <Section>
            <SectionTitle>
              Contraintes fournisseur
            </SectionTitle>

            {constraints.length ? (
              <ConstraintList>
                {constraints.map(
                  (constraint, index) => (
                    <ConstraintCard
                      key={constraintKey(
                        constraint,
                        index,
                      )}
                      $blocking={
                        Boolean(
                          constraint?.is_blocking,
                        )
                      }
                    >
                      <ConstraintHeading>
                        <strong>
                          {displayValue(
                            constraint.label
                            || constraint.name
                            || constraint.code
                            || constraint.type,
                            "Contrainte",
                          )}
                        </strong>
                        <ConstraintBadge
                          $blocking={
                            Boolean(
                              constraint?.is_blocking,
                            )
                          }
                        >
                          {constraint?.is_blocking
                            ? "Bloquante"
                            : "Informative"}
                        </ConstraintBadge>
                      </ConstraintHeading>

                      <ConstraintDetails>
                        <span>
                          Atteinte :{" "}
                          {displayBoolean(
                            constraint?.is_satisfied
                            ?? constraint?.satisfied,
                          )}
                        </span>

                        <span>
                          Montant actuel :{" "}
                          {displayValue(
                            constraint?.current_value
                            ?? constraint?.current_amount,
                          )}
                        </span>

                        <span>
                          Seuil :{" "}
                          {displayValue(
                            constraint?.required_value
                            ?? constraint?.threshold,
                          )}
                        </span>
                      </ConstraintDetails>

                      {constraint?.message && (
                        <ConstraintMessage>
                          {constraint.message}
                        </ConstraintMessage>
                      )}
                    </ConstraintCard>
                  ),
                )}
              </ConstraintList>
            ) : (
              <AdminEmptyState
                message="Aucune contrainte fournisseur n’est remontée."
              />
            )}
          </Section>
        </>
      )}
    </PanelRoot>
  );
}


function ProposalSummary({ proposal }) {
  return (
    <SummaryGrid aria-label="Résumé de la proposition">
      <SummaryCard>
        <span>Quantité totale</span>
        <strong>
          {displayValue(
            proposal?.total_quantity,
            "0",
          )}
        </strong>
      </SummaryCard>

      <SummaryCard>
        <span>Montant HT estimé</span>
        <strong>
          {displayValue(
            proposal?.estimated_total_ht,
            "Non estimé",
          )}
        </strong>
      </SummaryCard>

      <SummaryCard>
        <span>Fournisseur</span>
        <strong>
          {displayValue(
            proposal?.supplier_uuid,
            "Multi-fournisseurs",
          )}
        </strong>
      </SummaryCard>

      <SummaryCard>
        <span>Proposition valide</span>
        <strong>
          {displayBoolean(
            proposal?.is_valid,
          )}
        </strong>
      </SummaryCard>
    </SummaryGrid>
  );
}


const PanelRoot = styled.section`
  min-width: 0;
  color: #f7f7f7;
`;


const FieldGroup = styled.label`
  display: grid;
  flex: 1 1 210px;
  gap: 0.35rem;
`;


const FieldGroupWide = styled(FieldGroup)`
  flex: 2 1 340px;
`;


const FieldLabel = styled.span`
  color: rgba(247, 247, 247, 0.72);
  font-size: 0.8rem;
  font-weight: 700;
`;


const fieldStyles = `
  width: 100%;
  min-height: 42px;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 12px;
  background: #151d31;
  color: #f7f7f7;
  outline: none;
`;


const TextInput = styled.input`
  ${fieldStyles}
`;


const NumberInput = styled.input`
  ${fieldStyles}
`;


const TextArea = styled.textarea`
  ${fieldStyles}
  min-height: 72px;
  resize: vertical;
`;


const PurchaseOrderFeedback = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;

  &:empty {
    display: none;
  }
`;


const IntroCard = styled.div`
  display: grid;
  gap: 0.4rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);

  span {
    color: rgba(247, 247, 247, 0.68);
  }
`;


const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;


const SummaryCard = styled.article`
  display: grid;
  gap: 0.35rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);

  span {
    color: rgba(247, 247, 247, 0.6);
    font-size: 0.8rem;
  }

  strong {
    overflow-wrap: anywhere;
  }
`;


const Section = styled.section`
  margin-top: 1.25rem;
`;


const SectionTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1rem;
`;


const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
`;


const ResultTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem;
    border-bottom:
      1px solid rgba(255, 255, 255, 0.07);
    text-align: left;
    vertical-align: top;
  }

  th {
    color: rgba(247, 247, 247, 0.68);
    font-size: 0.76rem;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;


const ConstraintList = styled.div`
  display: grid;
  gap: 0.75rem;
`;


const ConstraintCard = styled.article`
  display: grid;
  gap: 0.65rem;
  padding: 0.9rem 1rem;
  border: 1px solid
    ${({ $blocking }) => (
      $blocking
        ? "rgba(255, 107, 107, 0.45)"
        : "rgba(255, 255, 255, 0.08)"
    )};
  border-radius: 14px;
  background:
    ${({ $blocking }) => (
      $blocking
        ? "rgba(255, 107, 107, 0.08)"
        : "rgba(255, 255, 255, 0.04)"
    )};
`;


const ConstraintHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;


const ConstraintBadge = styled.span`
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background:
    ${({ $blocking }) => (
      $blocking
        ? "rgba(255, 107, 107, 0.18)"
        : "rgba(92, 219, 149, 0.15)"
    )};
  font-size: 0.75rem;
  font-weight: 700;
`;


const ConstraintDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
  color: rgba(247, 247, 247, 0.68);
  font-size: 0.82rem;
`;


const ConstraintMessage = styled.p`
  margin: 0;
  color: rgba(247, 247, 247, 0.78);
`;
